import React from "react";
import { Link } from "react-router-dom";
import { useLanguage } from "@/contexts/LanguageContext";
import { ArrowUpRight } from "lucide-react";
import { StarRating } from "@/components/ProductReviews";

export default function ProductCard({ product, idx = 0, rating = null }) {
  const { lang, t } = useLanguage();
  const title = lang === "en" ? product.title_en : product.title_no;
  const short = lang === "en" ? product.short_en : product.short_no;

  return (
    <Link
      to={`/produkt/${product.slug}`}
      data-testid={`product-card-${product.slug}`}
      className="group block bg-white border border-[#E5E2DC] rounded-2xl overflow-hidden transition-all duration-500 hover:-translate-y-1 hover:shadow-[0_12px_40px_rgba(0,0,0,0.06)]"
      style={{ animationDelay: `${idx * 60}ms` }}
    >
      <div className="aspect-[4/5] overflow-hidden bg-[#F9F8F6] relative">
        <img
          src={product.image_url}
          alt={title}
          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
          loading="lazy"
        />
        {product.egen_utgivelse && (
          <div
            className="absolute top-4 left-4 bg-[#2C302E] text-white rounded-full pl-2 pr-3 py-1 text-[10px] font-semibold tracking-wider uppercase flex items-center gap-1.5 shadow-sm"
            data-testid={`product-card-badge-${product.slug}`}
            title="Skrevet av Naima Mtoor"
          >
            <span className="w-1.5 h-1.5 rounded-full bg-[#D97B66]" />
            Egen utgivelse
          </div>
        )}
        <div className="absolute top-4 right-4 bg-white/90 backdrop-blur-sm rounded-full px-3 py-1 text-xs font-medium text-[#2C302E]">
          {product.pages} {t("product_pages")}
        </div>
      </div>
      <div className="p-6">
        <div className="flex items-start justify-between gap-3 mb-2">
          <h3 className="font-heading text-lg md:text-xl text-[#2C302E] leading-snug font-medium">
            {title}
          </h3>
          <ArrowUpRight
            className="w-5 h-5 text-[#6B726C] flex-shrink-0 mt-0.5 group-hover:text-[#D97B66] transition-colors"
            strokeWidth={1.5}
          />
        </div>
        {rating && rating.count > 0 && (
          <div
            className="flex items-center gap-1.5 mb-2"
            data-testid={`product-card-rating-${product.slug}`}
          >
            <StarRating value={rating.average} size={12} />
            <span className="text-xs text-[#6B726C]">
              {rating.average.toFixed(1)} ({rating.count})
            </span>
          </div>
        )}
        <p className="text-sm text-[#6B726C] leading-relaxed line-clamp-2 mb-5">
          {short}
        </p>
        <div className="flex items-end justify-between pt-4 border-t border-[#E5E2DC]">
          <div>
            <p className="text-[10px] uppercase tracking-widest text-[#6B726C]">
              {t("product_price")}
            </p>
            <p className="font-heading text-xl text-[#2C302E] font-medium">
              {product.price_nok} <span className="text-sm text-[#6B726C]">NOK</span>
            </p>
          </div>
          <span className="text-xs text-[#8BA896] group-hover:text-[#D97B66] transition-colors">
            PDF →
          </span>
        </div>
      </div>
    </Link>
  );
}
